import { Suspense, useEffect, useRef } from 'react';
import { iconNames } from '@customerio/pluma-icons';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useLocation, useParams, useRouter } from '@tanstack/react-router';
import {
  Banner,
  Button,
  Link,
  Page,
  PageBody,
  PageHeader,
  PageHeaderActions,
  PageHeaderIconTile,
  PageHeaderLabel,
  PageHeaderTabs,
  TabNav,
  TabNavList,
  TabNavTab,
  type IconProps,
  PageHeaderTitle,
} from '@customerio/pluma-components/react';
import { DocumentTitle } from '@/components/page/document-title';
import { LoadingPage } from '@/components/page/loading';
import { useAnalytics } from '@/services/analytics';
import { getObjectTypeBySlug } from '@/services/db/object-type/entity';
import { useObjectTypes } from '@/services/db/object-type/queries';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { useCanConfigureObjects, useIsRedacted } from '@/services/permissions/hooks';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { generatePath } from '@/services/router/generate-path';
import { useRegisterBreadcrumbs } from '@/services/breadcrumbs';
import { ObjectListTable } from './components/object-list-table';
import { AddObjectDropdown } from './components/add-object-dropdown';
import { useAddRelationshipsModal } from './components/add-relationships-modal';
import { ObjectExport } from './components/object-export';
import { summarizeObjectFilters } from './components/object-filter-builder/object-filter-format';
import { useObjectColumns } from './use-object-columns';
import { queryableObjectFilters, useObjectList } from './use-object-list';
import type { WorkspaceEnvironment } from '@/services/db/environment/entity';
import { objectTypeRedirectHref } from './urls';

function objectIcon(icon: string | null | undefined): IconProps['name'] {
  return iconNames.find((name) => name === icon) ?? 'object';
}

function ObjectTypeContent() {
  const workspaceId = requireWorkspaceId();
  const { slug } = useParams({ strict: false });
  const router = useRouter();
  const location = useLocation();
  const { data: objectTypes } = useObjectTypes({ queryConfig: { staleTime: 0 } });
  const objectType = getObjectTypeBySlug(objectTypes, slug ?? '');
  const environment = useEnvironmentOptional().data?.environment;
  const canConfigure = useCanConfigureObjects();
  const isRedacted = useIsRedacted();
  const { hasPermission } = usePermissions();
  const rootHref = router.buildLocation({ to: '/workspaces/$workspaceId/objects', params: { workspaceId } }).href;

  useRegisterBreadcrumbs([
    { id: 'objects', text: 'Custom objects', href: rootHref },
    ...(objectType == null
      ? []
      : [
          {
            id: `objects.${objectType.id}`,
            text: objectType.name,
            titleText: `Object type ${objectType.id}`,
            className: 'fs-mask',
            isTruncated: true,
            href: router.buildLocation({
              to: '/workspaces/$workspaceId/objects/$slug',
              params: { workspaceId, slug: objectType.slug },
            }).href,
          },
        ]),
  ]);

  if (objectType == null) {
    const href = router.buildLocation({ to: '/workspaces/$workspaceId/objects', params: { workspaceId } }).href;
    return <SafeNavigate to={href} options={{ shouldReplaceHistoryEntry: true }} />;
  }
  if (slug !== objectType.slug) {
    const href = router.buildLocation({
      to: '/workspaces/$workspaceId/objects/$slug',
      params: { workspaceId, slug: objectType.slug },
    }).href;
    return (
      <SafeNavigate
        to={objectTypeRedirectHref(href, location.searchStr, location.hash)}
        options={{ shouldReplaceHistoryEntry: true }}
      />
    );
  }

  const manageHref = router.buildLocation({
    to: '/workspaces/$workspaceId/settings/objects/edit/$objectType',
    params: { workspaceId, objectType: objectType.id },
  }).href;
  const guideHref = generatePath('/workspaces/:workspaceId/journeys/objects/:objectSlug/guide', {
    workspaceId,
    objectSlug: objectType.slug,
  });

  return (
    <ObjectTypeResolved
      objectType={objectType}
      workspaceId={workspaceId}
      environment={environment}
      canConfigure={canConfigure}
      canCreate={hasPermission('journeys/objects/create')}
      canDelete={hasPermission('journeys/objects/delete')}
      canExport={hasPermission('journeys/exports/create')}
      canCreateRelationships={hasPermission('journeys/object_relationships/create')}
      shouldRedactCreatedAt={isRedacted && environment?.is_sample_workspace !== true}
      canViewSettings={hasPermission('journeys/object_types/view')}
      manageHref={manageHref}
      guideHref={guideHref}
    />
  );
}

type ResolvedObjectType = NonNullable<ReturnType<typeof getObjectTypeBySlug>>;

type ObjectTypeHeaderProps = {
  objectType: ResolvedObjectType;
  workspaceId: number;
  environment: WorkspaceEnvironment['environment'];
  canViewSettings: boolean;
  manageHref: string;
  guideHref: string;
  canCreate: boolean;
  canExport: boolean;
  canCreateRelationships: boolean;
  displayedAttributes: readonly string[];
  exportFilters: string | null;
  isExportBroaderThanList: boolean;
  isListLoaded: boolean;
};

function ObjectTypeHeader(props: ObjectTypeHeaderProps) {
  const router = useRouter();
  const relationshipsModal = useAddRelationshipsModal();
  const showTabs = props.objectType.has_non_ui_objects !== true || !props.objectType.enabled;
  return (
    <PageHeader>
      <PageHeaderIconTile name={objectIcon(props.objectType.icon)} />
      {!props.objectType.enabled ? <PageHeaderLabel>Disabled</PageHeaderLabel> : null}
      <PageHeaderTitle>{props.objectType.name}</PageHeaderTitle>
      <PageHeaderActions>
        <ObjectExport
          objectType={props.objectType}
          displayedAttributes={props.displayedAttributes}
          filters={props.exportFilters}
          isBroaderThanList={props.isExportBroaderThanList}
          isDisabled={!props.canExport || !props.isListLoaded}
        />
        <Button icon="manage" href={props.manageHref} isDisabled={!props.canViewSettings}>
          Manage
        </Button>
        <AddObjectDropdown
          objectType={props.objectType}
          workspaceId={props.workspaceId}
          canCreate={props.canCreate}
          canCreateRelationships={props.canCreateRelationships}
          onAddRelationships={() =>
            relationshipsModal.open(
              { objectType: props.objectType, workspaceId: props.workspaceId },
              { shouldCloseOnUnmount: true },
            )
          }
        />
      </PageHeaderActions>
      {showTabs ? (
        <PageHeaderTabs>
          {!props.objectType.enabled ? (
            <Banner variant="information">
              <Link variant="secondary" href="/settings/billing/available-plans/journeys">
                Your plan
              </Link>{' '}
              allows a maximum of {props.environment?.max_allowed_object_types ?? 0} object types.{' '}
              {props.objectType.name} is disabled and cannot be used in Segments or Messages.
            </Banner>
          ) : null}
          {props.objectType.has_non_ui_objects !== true ? (
            <TabNav aria-label={`${props.objectType.name} pages`}>
              <TabNavList>
                <TabNavTab href={props.guideHref} isActive={false}>
                  Setup guide
                </TabNavTab>
                <TabNavTab
                  href={
                    router.buildLocation({
                      to: '/workspaces/$workspaceId/objects/$slug',
                      params: { workspaceId: props.workspaceId, slug: props.objectType.slug },
                    }).href
                  }
                  isActive={true}
                >
                  {props.objectType.name}
                </TabNavTab>
              </TabNavList>
            </TabNav>
          ) : null}
        </PageHeaderTabs>
      ) : null}
    </PageHeader>
  );
}

type ObjectTypeResolvedProps = Omit<
  ObjectTypeHeaderProps,
  'displayedAttributes' | 'exportFilters' | 'isExportBroaderThanList' | 'isListLoaded'
> & {
  canConfigure: boolean;
  canDelete: boolean;
  shouldRedactCreatedAt: boolean;
};

function ObjectTypeResolved(props: ObjectTypeResolvedProps) {
  const state = useObjectList(props.objectType.id);
  const columns = useObjectColumns({
    workspaceId: props.workspaceId,
    objectTypeId: props.objectType.id,
    sentAttributes: props.objectType.sent_attributes ?? [],
    isSampleWorkspace: props.environment?.is_sample_workspace === true,
  });
  const { track } = useAnalytics();
  const tracked = useRef(0);
  const filterSummary = summarizeObjectFilters(state.filters);

  useEffect(() => {
    if (tracked.current === state.query.dataUpdatedAt) return;
    tracked.current = state.query.dataUpdatedAt;
    void track('objects', 'listViewedSearchedFiltered', {
      'Filter Operator': filterSummary.operator,
      'Number of Object Attribute Conditions': filterSummary.objectAttributes,
      'Number of Relationship Attribute Conditions': filterSummary.relationshipAttributes,
      'Has Enabled Objects': true,
      'Has Objects Data': state.objects.length > 0,
      'Filtered by Name': state.name !== '',
      'Name Filter Includes Wildcard': state.name.includes('*'),
      'Filtered by Without Relationships': state.withoutRelationships,
      'Filtered by With Relationships': state.withRelationships,
    });
  }, [
    filterSummary.objectAttributes,
    filterSummary.operator,
    filterSummary.relationshipAttributes,
    state.name,
    state.objects.length,
    state.query.dataUpdatedAt,
    state.withRelationships,
    state.withoutRelationships,
    track,
  ]);

  return (
    <>
      <DocumentTitle title={props.objectType.name} />
      <Page>
        <ObjectTypeHeader
          {...props}
          displayedAttributes={columns.availableColumns.filter(
            (attribute) => columns.columnVisibility[attribute] !== false,
          )}
          exportFilters={queryableObjectFilters(state.filters, props.objectType.id) ?? null}
          isExportBroaderThanList={state.name !== '' || state.withoutRelationships || state.withRelationships}
          isListLoaded={state.query.isSuccess}
        />
        <PageBody>
          <ObjectListTable
            objectType={props.objectType}
            objects={state.objects}
            total={state.total}
            page={state.page}
            name={state.name}
            filters={state.filters}
            activeFilterBy={state.activeFilterBy}
            withoutRelationships={state.withoutRelationships}
            withRelationships={state.withRelationships}
            message={state.message}
            isLoading={state.query.isFetching}
            canConfigure={props.canConfigure}
            canDelete={props.canDelete}
            shouldRedactCreatedAt={props.shouldRedactCreatedAt}
            availableColumns={columns.availableColumns}
            columnVisibility={columns.columnVisibility}
            onColumnVisibilityChange={columns.onColumnVisibilityChange}
            columnsSettings={columns.columnsSettings}
            rowSelection={state.rowSelection}
            allSelected={state.allSelected}
            allMatchingSelected={state.allMatchingSelected}
            deleteFilters={state.deleteFilters}
            onRowSelectionChange={state.setRowSelection}
            onSelectAll={state.selectAll}
            onClearSelection={state.clearSelection}
            onSearch={state.search}
            onApplyFilters={state.applyFilters}
            onActivateFilterBy={state.activateFilterBy}
            onPageChange={state.setPage}
            onSavedViewChange={state.setSavedView}
            workspaceId={props.workspaceId}
          />
        </PageBody>
      </Page>
    </>
  );
}

export default function ObjectTypePage() {
  return (
    <Suspense fallback={<LoadingPage />}>
      <ObjectTypeContent />
    </Suspense>
  );
}
